// Turns Thermals/Get into what HuntWind draws and says. Kept free of React and the map so it can be tested.
import { compass16, windStatus } from '../../../../../lib/weatherCenter'

export const RADIUS_KM = 2
// The service averages its ~31 m terrain pixels by a whole factor, so 60 comes back as ~62 m: 65 x 65 cells.
export const CELL_M = 60
// Every Nth cell gets an arrow: about 33 x 33 arrows, ~120 m apart.
export const ARROW_EVERY = 2
// Below this the slope wind is too weak to bother drawing.
const STILL_MS = 0.2

export const UPSLOPE = '#ff9e80'
export const DOWNSLOPE = '#7ec8ff'

// HuntWind's verdict colors: the stand's ideal winds green, close to one yellow, anything else red.
export const STATUS_COLORS = { ideal: '#7fc31c', risky: '#f5c518', bad: '#e02020' }
export const STATUS_WORDS = { ideal: 'ideal', risky: 'risky', bad: 'bad' }

const MS_TO_MPH = 2.23694
const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']

export const mph = ms => Math.round(ms * MS_TO_MPH)

// The bearing a vector points toward, degrees clockwise from north.
export const bearingOf = (u, v) => (Math.atan2(u, v) * 180 / Math.PI + 360) % 360
export const compassOf = deg => COMPASS[Math.round(deg / 45) % 8]

// Rates air moving toward (u, v) the way HuntWind rates the forecast wind: by the direction it comes
// FROM against the stand's ideal winds. Null when the stand has none, so nothing is called bad for it.
export function rate(u, v, preferred) {
  if (!preferred || preferred.length === 0) return null
  return windStatus((bearingOf(u, v) + 180) % 360, preferred)
}

// The cell under the field's centre, which is the stand. Thermals/Day frames carry no shade grid.
export function centreCell(field) {
  const row = Math.floor(field.rows / 2), col = Math.floor(field.cols / 2)
  const i = row * field.cols + col
  return { u: field.u[i], v: field.v[i], regime: field.regime[i], shaded: field.shaded ? field.shaded[i] : null }
}

// Where scent drifts: the forecast wind (blowing FROM `fromDeg` at `windMph`) plus the stand's slope wind.
export function scentDrift(windMph, fromDeg, cell) {
  const speed = Number.isFinite(windMph) ? windMph / MS_TO_MPH : 0
  const toward = ((Number.isFinite(fromDeg) ? fromDeg : 0) + 180) * Math.PI / 180
  const u = speed * Math.sin(toward) + (cell ? cell.u : 0)
  const v = speed * Math.cos(toward) + (cell ? cell.v : 0)
  const ms = Math.hypot(u, v)
  return { ms, mph: mph(ms), toward: compassOf(bearingOf(u, v)), bearing: bearingOf(u, v), u, v }
}

// One line for the square: is the sun on the slopes, and which way are they running.
export function headline(field) {
  const { summary, sun } = field
  const share = s => `${Math.round(s * 100)}%`
  const up = summary.upslope, down = summary.downslope
  const lead = sun.up ? 'Sun up' : 'After dark'
  if (up < 0.05 && down < 0.05) return `${lead} · slopes are still`
  const main = up >= down
    ? `running uphill on ${share(up)} of the ground`
    : `draining downhill on ${share(down)} of the ground`
  return `${lead} · ${main}, up to ${mph(summary.maxMs)} mph`
}

// The slopes are settled one way once most of the ground runs that way and the ground's mean sensible heat
// flux (W/m², positive warming) is past this either side; in between they are turning. Thermals/Day with
// its lag runs nights near -15 to -55 and noon near +100 to +130.
export const SETTLED_SHARE = 0.6
export const TURNING_WM2 = 10

// What the ground is doing to the air over the area: 'warming', 'cooling' or 'turning'; null when the
// service did not send the heat flux.
export function heatTrend(summary) {
  const flux = summary ? summary.meanHeatFluxWm2 : null
  if (typeof flux !== 'number' || !Number.isFinite(flux)) return null
  if (summary.upslope >= SETTLED_SHARE && flux > TURNING_WM2) return 'warming'
  if (summary.downslope >= SETTLED_SHARE && flux < -TURNING_WM2) return 'cooling'
  return 'turning'
}

// `place` names the centre, HuntWind's stand by default.
export function standLine(cell, place = 'the stand') {
  if (!cell || cell.regime === 0 || Math.hypot(cell.u, cell.v) < STILL_MS) return `At ${place}: no slope wind`
  const dir = compassOf(bearingOf(cell.u, cell.v))
  return `At ${place}: ${Math.max(1, mph(Math.hypot(cell.u, cell.v)))} mph ${cell.regime > 0 ? 'upslope' : 'downslope'}, toward ${dir}`
}

// The arrows to draw: a thinned grid of cells with a slope wind, each at its cell centre in lat/lng, with its
// rating against the stand's ideal winds (null without any).
export function arrows(field, every = ARROW_EVERY, preferred = []) {
  const { cols, rows, bounds } = field
  const dLng = (bounds.east - bounds.west) / cols
  const dLat = (bounds.north - bounds.south) / rows
  const out = []
  for (let r = Math.floor(every / 2); r < rows; r += every) {
    for (let c = Math.floor(every / 2); c < cols; c += every) {
      const i = r * cols + c
      const ms = Math.hypot(field.u[i], field.v[i])
      if (field.regime[i] === 0 || ms < STILL_MS) continue
      out.push({
        lat: bounds.north - (r + 0.5) * dLat,
        lng: bounds.west + (c + 0.5) * dLng,
        u: field.u[i],
        v: field.v[i],
        ms,
        up: field.regime[i] > 0,
        status: rate(field.u[i], field.v[i], preferred)
      })
    }
  }
  return out
}

// An arrow's color: its rating when the stand has ideal winds, else uphill / downhill.
export const arrowColor = a => (a.status ? STATUS_COLORS[a.status] : a.up ? UPSLOPE : DOWNSLOPE)

// ---- The scent plume ------------------------------------------------------------------

const M_PER_DEG_LAT = 111320
// The ribbon's color where the stand has no ideal winds to rate against.
export const PLUME_PLAIN = '#ffffff'

// Rates where scent has got to by the sector it is in from the stand, the way the ideal-wind wedges are drawn:
// a stand set for north winds wants its scent south of it. So a spot rates as the wind from the opposite
// bearing would: ideal inside the sectors, risky just outside an edge, bad well outside. Null when the
// stand has no ideal winds, or the spot is the stand itself.
export function sectorStatus(stand, at, preferred) {
  if (!preferred || preferred.length === 0) return null
  const mPerDegLng = M_PER_DEG_LAT * Math.cos(stand.lat * Math.PI / 180)
  const dx = (at.lng - stand.lng) * mPerDegLng, dy = (at.lat - stand.lat) * M_PER_DEG_LAT
  if (Math.hypot(dx, dy) < 1) return null
  return windStatus((bearingOf(dx, dy) + 180) % 360, preferred)
}

const WORST = { ideal: 0, risky: 1, bad: 2 }

// The plume's verdict: the worst sector any of it drifts into.
export function plumeStatus(ribbon) {
  return ribbon.reduce((worst, r) => (r.status && (!worst || WORST[r.status] > WORST[worst]) ? r.status : worst), null)
}

// The ribbon along the plume: at each point its left and right edges (half its width each side,
// across the local direction of travel), how strong the scent still is, and how its sector from the stand
// rates (at the stand itself, how the air leaving it rates).
export function plumeRibbon(plume, preferred = []) {
  const points = (plume && plume.points) || []
  const stand = points.length ? { lat: points[0].lat, lng: points[0].lon } : null
  return points.map((p, i) => {
    const a = points[Math.max(0, i - 1)], b = points[Math.min(points.length - 1, i + 1)]
    const mPerDegLng = M_PER_DEG_LAT * Math.cos(p.lat * Math.PI / 180)
    let dx = (b.lon - a.lon) * mPerDegLng, dy = (b.lat - a.lat) * M_PER_DEG_LAT
    if (Math.hypot(dx, dy) < 1e-6) { dx = p.u; dy = p.v }
    const d = Math.hypot(dx, dy) || 1
    // The left-hand normal of the direction of travel, scaled to half the width.
    const half = (p.widthM || 0) / 2
    const nx = -dy / d * half, ny = dx / d * half
    return {
      lat: p.lat,
      lng: p.lon,
      left: { lat: p.lat + ny / M_PER_DEG_LAT, lng: p.lon + nx / mPerDegLng },
      right: { lat: p.lat - ny / M_PER_DEG_LAT, lng: p.lon - nx / mPerDegLng },
      seconds: p.seconds == null ? i : p.seconds,
      strength: Math.max(0, Math.min(1, p.strength)),
      status: sectorStatus(stand, { lat: p.lat, lng: p.lon }, preferred) || rate(p.u, p.v, preferred)
    }
  })
}

// Where a puff of scent is once it has gone a share (0-1) of the plume's travel time, and how far across
// the plume it drifted (-1 the left edge, 1 the right): its place, half the plume's width there as a
// point on the edge, the scent's strength there, and how the puff's own spot rates from the stand.
export function puffAt(ribbon, share, side = 0, preferred = []) {
  if (ribbon.length < 2) return null
  const end = ribbon[ribbon.length - 1].seconds
  const s = Math.max(0, Math.min(1, share)) * end
  let i = 0
  while (i < ribbon.length - 2 && ribbon[i + 1].seconds < s) i++
  const a = ribbon[i], b = ribbon[i + 1]
  const t = b.seconds > a.seconds ? Math.max(0, Math.min(1, (s - a.seconds) / (b.seconds - a.seconds))) : 0
  const lerp = (p, q) => ({ lat: p.lat + (q.lat - p.lat) * t, lng: p.lng + (q.lng - p.lng) * t })
  const centre = lerp(a, b), edge = lerp(side < 0 ? a.left : a.right, side < 0 ? b.left : b.right)
  const k = Math.abs(side)
  const at = { lat: centre.lat + (edge.lat - centre.lat) * k, lng: centre.lng + (edge.lng - centre.lng) * k }
  return {
    ...at,
    edge,
    strength: a.strength + (b.strength - a.strength) * t,
    status: sectorStatus(ribbon[0], at, preferred) || (t < 0.5 ? a.status : b.status)
  }
}

export const plumeColor = status => (status ? STATUS_COLORS[status] : PLUME_PLAIN)

// Where the scent goes, in a line: how far, which way from the stand, and how it ends.
export function plumeLine(plume) {
  const points = (plume && plume.points) || []
  if (points.length < 2) return null
  const first = points[0], last = points[points.length - 1]
  const mPerDegLng = M_PER_DEG_LAT * Math.cos(first.lat * Math.PI / 180)
  const toward = compass16(bearingOf((last.lon - first.lon) * mPerDegLng, (last.lat - first.lat) * M_PER_DEG_LAT))
  const km = (plume.lengthM / 1000).toFixed(1)
  const minutes = Math.round(plume.minutes)
  if (plume.end === 'stalled') return `Scent carries ${km} km toward ${toward}, then stalls`
  if (plume.end === 'edge') return `Scent carries over ${km} km toward ${toward} in ${minutes} min`
  return `Scent carries ${km} km toward ${toward} in ${minutes} min`
}
